import * as React from 'react';

import {colors} from 'shells/colors/colors';
import {FormField} from 'shells/form/form-field';
import {HalfAndHalfIcon} from 'shells/icon/svg/half-and-half-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Switch} from 'shells/switch/switch';
import {TextField} from 'shells/textfield/textfield';

import type {EditionFragment as Edition} from 'nutshell-graphql-types';

import {WinnerBadge} from './winner-badge';

import styles from './a-b-tested-field.module.css';

type Props = {
    edition: Edition;
    label: string;
    placeholder: string;
    primaryText: string;
    alternateText: string;
    onChangePrimary: (arg1: string) => void;
    onChangeAlternative: (arg1: string) => void;
    onToggleIsComparing: () => void;
    isComparing: boolean;
    helperText?: string;
    showPreviewMode: boolean;
};

export function ABTestedField(props: Props) {
    const {
        edition,
        isComparing,
        primaryText,
        placeholder,
        helperText,
        alternateText,
        onChangePrimary,
        onChangeAlternative,
        onToggleIsComparing,
        showPreviewMode,
    } = props;

    let text;
    let comparisonComponent;

    switch (edition.status) {
        case 'COMPARISON_SENDING':
        case 'COMPARISON_WAITING':
            if (isComparing) {
                text = 'A/B testing…';
                comparisonComponent = (
                    <div style={{marginTop: '4px'}}>
                        <HalfAndHalfIcon size={15} />
                    </div>
                );
            }
            break;
        case 'SENT':
            if (isComparing) {
                text = 'A/B tested';
                comparisonComponent = (
                    <div style={{marginTop: '4px'}}>
                        <HalfAndHalfIcon size={15} />
                    </div>
                );
            }
            break;
        default:
            text = 'Test two versions';
            comparisonComponent = (
                <Switch isOn={isComparing} isRounded={true} onChange={onToggleIsComparing} />
            );
    }

    return (
        <div
            style={{
                background: !showPreviewMode ? colors.offWhite : 'none',
                padding: !showPreviewMode || isComparing ? '16px 12px' : '16px 0',
            }}
            className={
                isComparing ? styles['a-b-tested-field--comparing'] : styles['a-b-tested-field']
            }
        >
            <div className='flex align-center justify-sb'>
                <label className='font-size-normal'>{props.label}</label>
                <StackLegacy spacing={6} isHorizontal={true} alignCenter={true}>
                    {comparisonComponent}
                    <label className='font-size-normal'>{text}</label>
                </StackLegacy>
            </div>
            <FormField
                innerId='fieldVersionA'
                label={
                    isComparing ? (
                        <div className='flex align-center'>
                            Version A
                            {edition.comparison &&
                            edition.comparison.winnerEdition &&
                            edition.comparison.winnerEdition.id === edition.id ? (
                                <WinnerBadge isWinner={true} />
                            ) : null}
                        </div>
                    ) : undefined
                }
                noMargin={true}
                isFullWidth={true}
                helperText={helperText && !isComparing ? helperText : undefined}
            >
                <TextField
                    id='fieldVersionA'
                    // @ts-expect-error
                    name='fieldVersionA'
                    placeholder={placeholder}
                    value={primaryText}
                    onChange={onChangePrimary}
                    disabled={showPreviewMode}
                />
            </FormField>
            {isComparing ? (
                <FormField
                    innerId='fieldVersionB'
                    label={
                        <div className='flex align-center'>
                            Version B
                            {edition.comparison &&
                            edition.comparison.winnerEdition &&
                            edition.comparison.winnerEdition.id !== edition.id ? (
                                <WinnerBadge isWinner={true} />
                            ) : null}
                        </div>
                    }
                    noMargin={true}
                    isFullWidth={true}
                    helperText={helperText}
                >
                    <TextField
                        id='fieldVersionB'
                        // @ts-expect-error
                        name='fieldVersionB'
                        placeholder={placeholder}
                        value={alternateText}
                        onChange={onChangeAlternative}
                        disabled={showPreviewMode}
                    />
                </FormField>
            ) : null}
        </div>
    );
}
